import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FirebaseTSFirestore, Limit, Where } from 'firebasets/firebasetsFirestore/firebaseTSFirestore';
import { Subject, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { SearchService } from 'src/app/search.service';
import { Firestore } from 'firebase/firestore';
import { FirebaseTSAuth } from 'firebasets/firebasetsAuth/firebaseTSAuth';
import { PostData } from '../../models/post.model';
import { UserData } from '../../models/user.model';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.css']
})
export class SearchComponent implements OnInit {
  searchQuery: string = '';
  searchResults: any[] = [];
  suggestions: any[] = [];
  suggestions2: any[] = [];
  suggestionsUser: any[] = [];
  suggestionsPost: any[] = [];
  firestore = new FirebaseTSFirestore();
  auth = new FirebaseTSAuth();
  searchSubject: Subject<string> = new Subject();
  isLoading: boolean = false;
  selectedFilter: string = 'all';
  showSearchField: boolean = true;
  searchCache: { [key: string]: any[] } = {};
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  private cacheTimestamps: { [key: string]: number } = {};

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private searchService: SearchService,
    private snackBar: MatSnackBar
  ) {
    this.setupSearchSubscription();
  }

  private setupSearchSubscription() {
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(query => {
        this.isLoading = true;
        return new Observable(observer => {
          this.provideSuggestions(query)
            .then(() => {
              this.isLoading = false;
              observer.next();
              observer.complete();
            })
            .catch(error => {
              this.isLoading = false;
              observer.error(error);
            });
        });
      })
    ).subscribe();
  }

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      if (params['q']) {
        this.searchQuery = params['q'];
        this.showSearchField = false; // Masquer le champ si on vient d'une recherche externe
        
        if (params['type'] === 'post') {
          this.selectedFilter = 'posts';
        }
        
        this.onSearch();
      }
    });

    this.searchService.currentSearchQuery.subscribe(query => {
      this.searchQuery = query;
    });
  }

  clearSearch() {
    this.searchQuery = '';
    this.suggestions = [];
    this.suggestions2 = [];
    this.suggestionsUser = [];
    this.suggestionsPost = [];
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { q: null },
      queryParamsHandling: 'merge'
    });
  }

  onFilterChange() {
    if (this.searchQuery) {
      this.onSearch();
    }
  }

  onSearchChange(query: string): void {
    this.searchService.updateSearchQuery(query);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { q: query },
      queryParamsHandling: 'merge'
    });
  }

  onSearch() {
    this.searchSubject.next(this.searchQuery);
  }

  async provideSuggestions(query: string): Promise<void> {
    if (query.trim() === '') {
      this.clearSuggestions();
      return;
    }

    const searchType = this.selectedFilter;
    const cacheKey = `${searchType}-${query}`;

    if (this.isCacheValid(cacheKey)) {
      this.loadFromCache(cacheKey);
      return;
    }

    try {
      if (searchType === 'all' || searchType === 'users') {
        await this.searchUsers(query);
      }

      if (searchType === 'all' || searchType === 'posts') {
        await this.searchPosts(query);
      }

      this.updateCache(cacheKey);
    } catch (error) {
      console.error('Erreur lors de la recherche:', error);
      this.snackBar.open('Une erreur est survenue lors de la recherche', 'Fermer', {
        duration: 3000
      });
    }
  }

  private clearSuggestions() {
    this.suggestions = [];
    this.suggestions2 = [];
    this.suggestionsUser = [];
    this.suggestionsPost = [];
  }

  private isCacheValid(cacheKey: string): boolean {
    const timestamp = this.cacheTimestamps[cacheKey];
    return timestamp && (Date.now() - timestamp) < this.CACHE_DURATION;
  }

  private loadFromCache(cacheKey: string) {
    const cachedResults = this.searchCache[cacheKey];
    if (cachedResults) {
      this.suggestions = cachedResults.filter(item => item.type === 'user');
      this.suggestionsUser = this.suggestions;
      this.suggestions2 = cachedResults.filter(item => !item.type || item.type !== 'user');
      this.suggestionsPost = this.suggestions2;
    }
  }

  private updateCache(cacheKey: string) {
    this.searchCache[cacheKey] = [...this.suggestions, ...this.suggestions2];
    this.cacheTimestamps[cacheKey] = Date.now();
  }

  private async searchUsers(query: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.suggestionsUser = [];
      this.suggestions = [];
      
      // Si la requête est vide, ne pas faire de recherche
      if (!query.trim()) {
        resolve();
        return;
      }

      const searchTerm = query.toLowerCase().trim();
      console.log('Searching for term:', searchTerm);
      
      // Récupérer tous les utilisateurs pour déboguer
      this.firestore.getCollection({
        path: ['Users'],
        where: [
          new Limit(200)
        ],
        onComplete: (result) => {
          console.log('All users in database:', result.docs.map(doc => {
            const data = doc.data();
            return {
              publicName: data.publicName,
              uid: data.uid
            };
          }));

          this.suggestions = result.docs.map(doc => {
            const userData = doc.data() as UserData;
            const displayName = userData.publicName || '';
            return {
              displayText: displayName,
              type: 'user',
              ...userData
            };
          }).filter(user => {
            const userName = user.displayText.toLowerCase();
            const matches = userName.includes(searchTerm);
            console.log(`Comparing "${userName}" with "${searchTerm}": ${matches}`);
            return matches;
          });
          
          console.log('Final suggestions:', this.suggestions);
          this.suggestionsUser = this.suggestions;
          resolve();
        },
        onFail: (error) => {
          console.error('Search failed:', error);
          reject(error);
        }
      });
    });
  }

  private async searchPosts(query: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.suggestionsPost = [];
      this.suggestions2 = [];

      // Si la requête est vide, ne pas faire de recherche
      if (!query.trim()) {
        resolve();
        return;
      }

      const searchTerm = query.toLowerCase().trim();
      console.log('Searching posts for term:', searchTerm);
      
      this.firestore.getCollection({
        path: ['Posts'],
        where: [
          new Limit(200)
        ],
        onComplete: (result) => {
          console.log('All posts in database:', result.docs.map(doc => {
            const data = doc.data();
            return {
              comment: data.comment,
              postId: data.postId
            };
          }));

          this.suggestions2 = result.docs.map(doc => {
            const data = doc.data();
            const comment = data.comment || '';
            return {
              displayText: comment,
              ...data
            };
          }).filter(post => {
            const postText = post.displayText.toLowerCase();
            const matches = postText.includes(searchTerm);
            console.log(`Comparing post "${postText}" with "${searchTerm}": ${matches}`);
            return matches;
          });

          this.suggestionsPost = this.suggestions2.map(element => ({
            comment: element.displayText,
            creatorId: element.creatorId,
            postId: element.postId,
            timestamp: element.timestamp,
            type: element.type,
            imageUrl: element.imageUrl,
            audioUrl: element.audioUrl,
            videoUrl: element.videoUrl,
            likes: element.likes,
            comments: element.comments,
            shares: element.shares,
            agribox: element.agribox
          }));

          console.log('Final post suggestions:', this.suggestionsPost);
          resolve();
        },
        onFail: (error) => {
          console.error('Search posts failed:', error);
          reject(error);
        }
      });
    });
  }

  async followUser(user: any) {
    try {
      const currentUser = this.auth.getAuth().currentUser;
      if (!currentUser) {
        this.snackBar.open('Vous devez être connecté pour suivre un utilisateur', 'Fermer', {
          duration: 3000
        });
        return;
      }

      user.isFollowed = !user.isFollowed;
      // Mettre à jour la base de données ici
      
      this.snackBar.open(
        user.isFollowed ? `Vous suivez maintenant ${user.publicName}` : `Vous ne suivez plus ${user.publicName}`,
        'Fermer',
        { duration: 2000 }
      );
    } catch (error) {
      console.error('Erreur lors du suivi/désabonnement:', error);
      this.snackBar.open('Une erreur est survenue', 'Fermer', {
        duration: 3000
      });
    }
  }
}
